import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DatabaseBackup, FlaskConical, Loader2, UploadCloud } from "lucide-react";

const IS_TEST_ENV =
  typeof window !== "undefined" &&
  (/\.preview\.emergentagent\.com$/.test(window.location.hostname) || window.location.hostname === "localhost");

function CredFields({ prodUrl, setProdUrl, email, setEmail, password, setPassword, idPrefix }) {
  return (
    <>
      <div className="space-y-1">
        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Production URL</Label>
        <Input value={prodUrl} onChange={(e) => setProdUrl(e.target.value)} className="rounded-sm" data-testid={`${idPrefix}-url`} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Production Admin Email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" className="rounded-sm" data-testid={`${idPrefix}-email`} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Production Admin Password</Label>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className="rounded-sm" data-testid={`${idPrefix}-password`} />
      </div>
    </>
  );
}

export function TestEnvBanner() {
  const [open, setOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [prodUrl, setProdUrl] = useState("https://replenishsupply.com");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  if (!IS_TEST_ENV) return null;

  const run = async () => {
    if (!prodUrl.trim() || !email.trim() || !password) { toast.error("Enter the production URL and your production admin credentials"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/refresh-from-production", { prod_url: prodUrl.trim(), email: email.trim(), password });
      toast.success(`Copied ${data.documents.toLocaleString()} records from production. Please log in again with your production credentials.`, { duration: 6000 });
      setTimeout(() => { window.location.href = "/"; }, 2500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Refresh failed");
      setBusy(false);
    }
  };

  const runPush = async () => {
    if (!prodUrl.trim() || !email.trim() || !password) { toast.error("Enter the production URL and your production admin credentials"); return; }
    if (confirmText.trim() !== "PUSH LIVE") { toast.error('Type "PUSH LIVE" to confirm — this replaces all live data'); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push-to-production", { prod_url: prodUrl.trim(), email: email.trim(), password });
      toast.success(`Pushed ${data.documents.toLocaleString()} records to the live site. A backup of the previous live data was saved on the server.`, { duration: 8000 });
      setPushOpen(false);
      setConfirmText("");
      setBusy(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Push failed");
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-400 px-4 py-1.5" data-testid="test-env-banner">
        <FlaskConical className="h-3.5 w-3.5 shrink-0 text-amber-950" />
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-950">Test Environment — changes here never affect the live app</span>
        <button type="button" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 rounded-sm border border-amber-950/30 px-2 py-0.5 text-[11px] font-semibold text-amber-950 transition-colors hover:bg-amber-300"
          data-testid="refresh-from-prod-button">
          <DatabaseBackup className="h-3 w-3" /> Refresh from Production
        </button>
        <button type="button" onClick={() => setPushOpen(true)}
          className="inline-flex items-center gap-1 rounded-sm border border-red-800/40 bg-red-700 px-2 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-red-800"
          data-testid="push-to-prod-button">
          <UploadCloud className="h-3 w-3" /> Push to Live Site
        </button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="refresh-prod-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Refresh Test Data from Production</DialogTitle>
            <DialogDescription>
              One-way copy: production is only read, never changed. The current test data is backed up, then replaced with a fresh copy of the live database.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <CredFields {...{ prodUrl, setProdUrl, email, setEmail, password, setPassword }} idPrefix="refresh-prod" />
            <p className="text-xs text-amber-700">After the copy you'll be signed out — log back in with your production credentials.</p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} disabled={busy} data-testid="refresh-prod-cancel">Cancel</Button>
              <Button onClick={run} disabled={busy} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="refresh-prod-run">
                {busy ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Copying…</>) : "Copy Production Data"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pushOpen} onOpenChange={(o) => { if (!busy) { setPushOpen(o); if (!o) setConfirmText(""); } }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="push-prod-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-red-700">Push Test Data to the Live Site</DialogTitle>
            <DialogDescription>
              This <span className="font-semibold text-red-700">replaces ALL data on the live site</span> with a copy of this test database. The current live data is automatically backed up on the server first. The live app must be running the latest deployed code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <CredFields {...{ prodUrl, setProdUrl, email, setEmail, password, setPassword }} idPrefix="push-prod" />
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wide text-red-700">Type "PUSH LIVE" to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="PUSH LIVE" className="rounded-sm border-red-300" data-testid="push-prod-confirm" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="rounded-sm" onClick={() => setPushOpen(false)} disabled={busy} data-testid="push-prod-cancel">Cancel</Button>
              <Button onClick={runPush} disabled={busy || confirmText.trim() !== "PUSH LIVE"} className="rounded-sm bg-red-700 text-white hover:bg-red-800" data-testid="push-prod-run">
                {busy ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Pushing…</>) : "Push to Live Site"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
